import { fullLabel } from "./WhereItIs";
import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { provTap } from "@/lib/provenance";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PhotoUploader } from "@/components/PhotoUploader";
import { ScanField } from "@/components/ScanField";
import { resolveScannedTag, tagStatusWords } from "@/lib/tagScan";
import { toast } from "sonner";

import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { RotateCcw, Trash2, AlertTriangle, Camera, Truck, Recycle, Plus, TicketX, Tag as TagIcon, Undo2 } from "lucide-react";
import { RETURN_REASONS } from "@/lib/returnReasons";

// ONE implementation, shared by all three fresh-tag boxes in this file (void → re-tag,
// re-tag, un-scrap → re-tag): whatever the scanner produced becomes the FAS number
// printed on the physical tag, and a tag that is already spoken for says so HERE rather
// than after the operator has filled in a reason and pressed the button.
async function setFreshTag(raw, setNewTag) {
  const r = await resolveScannedTag(raw);
  if (!r.code) { scanRefuse(r.error || "COULDN'T READ THAT TAG"); toast.error(r.error || "Couldn't read that tag."); return; }
  if (r.boundPart || r.status === "voided" || r.status === "retired") {
    scanRefuse(tagStatusWords(r)); toast.error(tagStatusWords(r)); return;
  }
  scanAccept(r.code);
  setNewTag(r.code);
}

// ---------------- Return to stock ----------------
export function ReturnDialog({ part, trigger, onDone }) {
  const [open, setOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [bin, setBin] = useState("");
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) api.get("/locations").then(({ data }) => setLocations(data.filter((l) => l.type !== "site"))).catch(() => {}); }, [open]);

  const submit = async () => {
    if (!bin) { toast.error("Pick a destination bin."); return; }
    if (!reason) { toast.error("Pick a reason — why is it coming back?"); return; }
    if (reason === "other" && note.trim().length < 2) { toast.error("Add a short note for an 'Other' reason."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/returns/scan", { part_scan: part.tag_code || part.short_code, location_id: bin,
        reason, reason_note: reason === "other" ? note.trim() : "",
        provenance: provTap("part_detail") });
      if (data.ok) {
        toast.success(`Back in stock${data.reversal ? ` — reversed ${data.reversal.credit_type} credit` : ""}`);
        setOpen(false); setReason(null); setNote(""); setBin(""); onDone?.();
      } else { toast.error(data.message); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="return-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><RotateCcw className="w-5 h-5 text-[#B01E28]" />Return to stock</DialogTitle>
          <DialogDescription>Put {part.short_code} back in a bin. Status returns to Available and the recovery credit from its original removal is reversed. If its order is already signed off, this is logged as a dated <span className="font-semibold">post-sign-off correction</span> on that closed order — the order itself stays as it was.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Why is it coming back?</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {RETURN_REASONS.map((r) => (
                <button key={r.code} data-testid={`return-reason-${r.code}`} onClick={() => setReason(r.code)}
                  className={`rounded-full px-4 py-2.5 font-semibold text-sm border-2 transition-colors ${reason === r.code ? "border-[#B01E28] bg-red-50 text-[#B01E28]" : "border-slate-200 text-slate-600 hover:border-[#B01E28]"}`}>
                  {r.label}
                </button>
              ))}
            </div>
            {reason === "other" && (
              <Textarea data-testid="return-reason-note" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Briefly, what happened?" className="mt-2 rounded-lg" rows={2} />
            )}
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Destination bin</Label>
            <Select value={bin} onValueChange={setBin}>
              <SelectTrigger data-testid="return-bin-select" className="rounded-lg border-slate-200 mt-1.5"><SelectValue placeholder="Pick a bin / container" /></SelectTrigger>
              <SelectContent className="rounded-lg max-h-72">{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.short_code} · {fullLabel(l)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="return-confirm" onClick={submit} disabled={busy || !bin || !reason || (reason === "other" && note.trim().length < 2)} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">{busy ? "Returning…" : "Return to stock"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Scrap / write-off ----------------
export function ScrapDialog({ part, trigger, onDone }) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState("defective");   // defective (throwout $0) | recycled (bulk)
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/removal-reasons").then(({ data }) => setReasons(data.filter((r) => r.key !== "recycle"))).catch(() => {});
    api.get("/recycle-batches", { params: { status: "open" } }).then(({ data }) => setBatches(data.batches || [])).catch(() => {});
  }, [open]);

  const selected = reasons.find((r) => r.key === reason);

  const newBatch = async () => {
    try { const { data } = await api.post("/recycle-batches", {}); setBatches((b) => [data, ...b]); setBatchId(data.id); toast.success(`Opened ${data.batch_number}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submit = async () => {
    if (note.trim().length < 3) { toast.error("Add a short note."); return; }
    setBusy(true);
    try {
      if (outcome === "recycled") {
        if (!batchId) { toast.error("Pick or open a recycle batch."); setBusy(false); return; }
        await api.post(`/recycle-batches/${batchId}/add`, { part_id: part.id, note: note.trim() });
        toast.success(`${part.short_code} recycled into ${batches.find((b) => b.id === batchId)?.batch_number || "batch"}`);
      } else {
        if (!reason) { toast.error("Pick a reason."); setBusy(false); return; }
        await api.post(`/parts/${part.id}/scrap`, { reason, note: note.trim() });
        toast.success(`Written off — ${selected?.label}`);
      }
      setOpen(false); setReason(""); setNote(""); setBatchId(""); setOutcome("defective"); onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const OutcomeBtn = ({ val, icon: Icon, label, sub }) => (
    <button data-testid={`scrap-outcome-${val}`} onClick={() => setOutcome(val)} type="button"
      className={`flex-1 rounded-lg border-2 p-3 text-left transition-all ${outcome === val ? (val === "recycled" ? "border-zinc-600 bg-zinc-50" : "border-[#B01E28] bg-red-50") : "border-slate-200 hover:border-slate-300"}`}>
      <div className="flex items-center gap-1.5 font-bold text-sm"><Icon className={`w-4 h-4 ${val === "recycled" ? "text-zinc-600" : "text-[#B01E28]"}`} />{label}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="scrap-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Trash2 className="w-5 h-5 text-[#B01E28]" />Scrap {part.short_code}</DialogTitle>
          <DialogDescription>Leaves inventory and is archived (never deleted) — and it stays visible on its donor truck forever.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <OutcomeBtn val="defective" icon={Trash2} label="Throw out" sub="Defective — $0 recovery" />
            <OutcomeBtn val="recycled" icon={Recycle} label="Recycle" sub="Into a bulk batch" />
          </div>
          {outcome === "defective" ? (
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="scrap-reason-select" className="rounded-lg border-slate-200 mt-1"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                <SelectContent className="rounded-lg">{reasons.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Recycle batch</Label>
              <div className="flex gap-2 mt-1">
                <Select value={batchId} onValueChange={setBatchId}>
                  <SelectTrigger data-testid="scrap-batch-select" className="rounded-lg border-slate-200 flex-1"><SelectValue placeholder={batches.length ? "Pick an open batch" : "No open batches"} /></SelectTrigger>
                  <SelectContent className="rounded-lg">{batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.batch_number}{b.material ? ` · ${b.material}` : ""} · {b.part_count} parts</SelectItem>)}</SelectContent>
                </Select>
                <Button data-testid="scrap-new-batch" type="button" variant="outline" onClick={newBatch} className="rounded-lg border-slate-300 shrink-0"><Plus className="w-4 h-4 mr-1" />New</Button>
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Recycling value is entered in bulk when the batch is closed — never per part.</p>
            </div>
          )}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Note</Label>
            <Textarea data-testid="scrap-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={outcome === "recycled" ? "e.g. stripped for scrap steel" : "What's wrong / why scrap?"} className="rounded-lg border-slate-200 mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="scrap-confirm" onClick={submit} disabled={busy}
            className={`rounded-lg text-white ${outcome === "recycled" ? "bg-zinc-700 hover:bg-zinc-800" : "bg-[#B01E28] hover:bg-[#8f1820]"}`}>
            {busy ? "Working…" : outcome === "recycled" ? "Add to recycle batch" : "Confirm write-off"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Scrap remaining truck (donor) ----------------
export function ScrapTruckDialog({ donor, trigger, onDone }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const match = confirmText.trim().toUpperCase() === (donor.stock_number || "").toUpperCase();

  const submit = async () => {
    if (!match) { toast.error("Type the stock number to confirm."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/donors/${donor.id}/scrap-truck`, { confirm: true, note: note.trim() });
      toast.success(`Truck scrapped — ${data.scrapped_parts} on-donor part(s) archived`);
      setOpen(false); setConfirmText(""); onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="scrap-truck-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-[#B01E28]"><AlertTriangle className="w-5 h-5" />Scrap remaining truck</DialogTitle>
          <DialogDescription>This marks donor <b>{donor.stock_number}</b> SCRAPPED and archives every part still on the truck. This cannot be undone by a single tap.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Type <b>{donor.stock_number}</b> to confirm</Label>
            <Input data-testid="scrap-truck-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={donor.stock_number} className="rounded-lg border-slate-200 mt-1 font-mono" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. rolled, total loss" className="rounded-lg border-slate-200 mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="scrap-truck-confirm" onClick={submit} disabled={busy || !match} className="rounded-lg bg-[#B01E28] hover:bg-[#8f1820] text-white"><Truck className="w-4 h-4 mr-1.5" />{busy ? "Scrapping…" : "Scrap this truck"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------- Void tag (mis-assigned / duplicate) — NOT a scrap ----------------
// Kills ONLY the bad tag number (dead forever). The part record stays 100% intact. Optionally
// re-tag right now with a fresh scanned tag, or defer to a "needs re-tag" blocked-scan state.
export function VoidTagDialog({ part, trigger, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState("retag");     // retag (fresh tag now) | defer (needs re-tag later)
  const [newTag, setNewTag] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setReason(""); setMode("retag"); setNewTag(""); };

  const submit = async () => {
    if (reason.trim().length < 3) { toast.error("Add a short reason (e.g. duplicate tag error)."); return; }
    if (mode === "retag" && !newTag.trim()) { toast.error("Scan or type the fresh tag, or switch to 'no tag yet'."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/parts/${part.id}/void-tag`, {
        reason: reason.trim(), new_tag_code: mode === "retag" ? newTag.trim() : null });
      toast.success(data.retagged ? `Bad tag voided → re-tagged ${data.new_tag}` : `Tag voided — part now needs a re-tag`);
      setOpen(false); reset(); onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="void-tag-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><TicketX className="w-5 h-5 text-[#B01E28]" />Void tag on {part.short_code}</DialogTitle>
          <DialogDescription>For a MIS-ASSIGNED or duplicate tag. Kills only this tag number (dead forever) — the part keeps all its data, value and history. This is <b>not</b> a scrap and moves no money.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Reason</Label>
            <Textarea data-testid="void-tag-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. duplicate tag error — wrong sticker applied" className="rounded-lg border-slate-200 mt-1" />
          </div>
          <div className="flex gap-2">
            <button type="button" data-testid="void-tag-mode-retag" onClick={() => setMode("retag")}
              className={`flex-1 rounded-lg border-2 p-3 text-left transition-all ${mode === "retag" ? "border-[#E0781A] bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
              <div className="flex items-center gap-1.5 font-bold text-sm"><TagIcon className="w-4 h-4 text-[#E0781A]" />Re-tag now</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Scan a fresh tag</div>
            </button>
            <button type="button" data-testid="void-tag-mode-defer" onClick={() => setMode("defer")}
              className={`flex-1 rounded-lg border-2 p-3 text-left transition-all ${mode === "defer" ? "border-slate-500 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}>
              <div className="flex items-center gap-1.5 font-bold text-sm"><AlertTriangle className="w-4 h-4 text-slate-500" />No tag yet</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Mark "needs re-tag"</div>
            </button>
          </div>
          {mode === "retag" && (
            <div>
              <ScanField testid="void-tag-scan" onValueChange={(v) => setNewTag(v)} label="Fresh tag" placeholder="Scan gun · camera · or type a tag…" submitLabel="Set tag" uppercase autoFocus={false} onScan={(v) => setFreshTag(v, setNewTag)} />
              {newTag && <p className="text-xs text-slate-600 mt-1">New tag: <span className="font-mono font-bold">{newTag}</span></p>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="void-tag-confirm" onClick={submit} disabled={busy} className="rounded-lg bg-[#B01E28] hover:bg-[#8f1820] text-white">{busy ? "Working…" : "Void tag"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Re-tag a part that is in the "needs re-tag" state ----------------
export function RetagDialog({ part, trigger, onDone }) {
  const [open, setOpen] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!newTag.trim()) { toast.error("Scan or type the fresh tag."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/parts/${part.id}/retag`, { new_tag_code: newTag.trim() });
      toast.success(`Re-tagged ${part.short_code} → ${data.new_tag}`);
      setOpen(false); setNewTag(""); onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setNewTag(""); }}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="retag-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><TagIcon className="w-5 h-5 text-[#E0781A]" />Re-tag {part.short_code}</DialogTitle>
          <DialogDescription>Marry this part to a fresh physical tag. Nothing else on the part changes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <ScanField testid="retag-scan" onValueChange={(v) => setNewTag(v)} label="Fresh tag" placeholder="Scan gun · camera · or type a tag…" submitLabel="Set tag" uppercase autoFocus onScan={(v) => setFreshTag(v, setNewTag)} />
          {newTag && <p className="text-xs text-slate-600">New tag: <span className="font-mono font-bold">{newTag}</span></p>}
        </div>
        <DialogFooter>
          <Button data-testid="retag-confirm" onClick={submit} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">{busy ? "Re-tagging…" : "Re-tag part"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Un-scrap / reclassify a mistakenly scrapped part ----------------
export function UnscrapDialog({ part, trigger, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState("retag");
  const [newTag, setNewTag] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setReason(""); setMode("retag"); setNewTag(""); };

  const submit = async () => {
    if (reason.trim().length < 3) { toast.error("Add a short reason for the reversal."); return; }
    if (mode === "retag" && !newTag.trim()) { toast.error("Scan the fresh tag, or switch to 'no tag yet'."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/parts/${part.id}/unscrap`, {
        reason: reason.trim(), new_tag_code: mode === "retag" ? newTag.trim() : null });
      toast.success(data.retagged ? `Restored → ${data.status} · re-tagged ${data.new_tag}` : `Restored → ${data.status} · needs re-tag`);
      setOpen(false); reset(); onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="unscrap-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Undo2 className="w-5 h-5 text-emerald-600" />Reverse scrap on {part.short_code}</DialogTitle>
          <DialogDescription>Undo a mistaken write-off. The part comes back with ALL its data (grade, notes, photos, donor-tie). Scrap moved no money, so nothing reverses financially. Its old tag was retired forever — assign a fresh one now or later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Reason for reversal</Label>
            <Textarea data-testid="unscrap-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. was a duplicate-tag data-entry mistake, not a real scrap" className="rounded-lg border-slate-200 mt-1" />
          </div>
          <div className="flex gap-2">
            <button type="button" data-testid="unscrap-mode-retag" onClick={() => setMode("retag")}
              className={`flex-1 rounded-lg border-2 p-3 text-left transition-all ${mode === "retag" ? "border-[#E0781A] bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
              <div className="flex items-center gap-1.5 font-bold text-sm"><TagIcon className="w-4 h-4 text-[#E0781A]" />Re-tag now</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Scan a fresh tag</div>
            </button>
            <button type="button" data-testid="unscrap-mode-defer" onClick={() => setMode("defer")}
              className={`flex-1 rounded-lg border-2 p-3 text-left transition-all ${mode === "defer" ? "border-slate-500 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}>
              <div className="flex items-center gap-1.5 font-bold text-sm"><AlertTriangle className="w-4 h-4 text-slate-500" />No tag yet</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Mark "needs re-tag"</div>
            </button>
          </div>
          {mode === "retag" && (
            <div>
              <ScanField testid="unscrap-scan" onValueChange={(v) => setNewTag(v)} label="Fresh tag" placeholder="Scan gun · camera · or type a tag…" submitLabel="Set tag" uppercase autoFocus={false} onScan={(v) => setFreshTag(v, setNewTag)} />
              {newTag && <p className="text-xs text-slate-600 mt-1">New tag: <span className="font-mono font-bold">{newTag}</span></p>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="unscrap-confirm" onClick={submit} disabled={busy} className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">{busy ? "Restoring…" : "Reverse scrap"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
